import { NetworkStatus, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  GET_ASSET_3D_INFO_QUERY,
  GET_BOX_FOLDER_ACCESS_TOKEN_QUERY,
  GET_OWN_CUSTOMER_ASSET_BY_UUID_QUERY,
  GET_OWN_CUSTOMER_ASSET_PARTS_BY_UUID_QUERY,
  GET_OWN_OEM_ASSET_BY_ID_QUERY,
  GET_OWN_OEM_ASSET_PARTS_BY_ID_QUERY,
  GET_SHARED_OEM_ASSET_BY_ID_QUERY,
  LIST_ALL_OWN_OEM_ASSETS_QUERY,
  LIST_CUSTOMER_PORTAL_SHARED_ASSETS_QUERY,
  LIST_OWN_OEM_ASSETS_CALENDAR_QUERY,
  GET_SUB_ASSETS_FOR_PROCEDURE_QUERY,
  LIST_OWN_OEM_KANBAN_ASSETS,
  LIST_REQUESTABLE_ASSETS_QUERY,
  LIST_OWN_OEM_MACHINE_HISTORY,
  LIST_OWN_OEM_SHARED_ASSET_HISTORY,
} from "~/api";
import { ITEMS_PER_PAGE } from "~/constants";

const EMPTY_SORT: string[] = [];

// Box token TTL is 59 min; polling at half that guarantees two refresh
// opportunities per token lifetime and stays well inside the BE's 5-min
// refresh window.
const BOX_TOKEN_POLL_INTERVAL_MS = 30 * 60 * 1000;
import {
  defaultFilters,
  generateAssetsQueryWhereCond,
} from "~/utils/filter/assetsQueryCond";

/**
 * Returns the work order's assets as a structured parent -> children tree, each
 * node carrying an `isAccessible` flag indicating whether the current user's
 * teams grant access. Used by the procedure AssetField to build the selectable
 * tree and to split already-selected sub-assets into removable (accessible) and
 * read-only (restricted) ones.
 */
export const useGetSubAssetsForProcedure = ({
  ticketId,
  skipCondition = false,
}: {
  ticketId?: string;
  skipCondition?: boolean;
} = {}) => {
  const {
    data: { getSubAssetsForProcedure = [] } = {},
    loading,
    error,
  } = useQuery(GET_SUB_ASSETS_FOR_PROCEDURE_QUERY, {
    variables: { ticketId },
    fetchPolicy: "cache-and-network",
    skip: skipCondition || !ticketId,
  });

  return {
    procedureAssets: getSubAssetsForProcedure,
    loading,
    error,
  };
};

export const useListOwnOemMachineHistory = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  where = null,
  includeHierarchy = false,
  selectedMachineIds = undefined,
  skipCondition = false,
}: {
  limit?: number;
  skip?: number;
  searchQuery?: string;
  where?: any;
  includeHierarchy?: boolean;
  selectedMachineIds?: string[];
  skipCondition?: boolean;
} = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      where: {
        searchQuery,
        ...(where || {}),
      },
    },
    includeHierarchy,
    selectedMachineIds,
  };

  const {
    data: { listOwnOemMachineHistory = {} } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_MACHINE_HISTORY, {
    variables,
    skip: skipCondition,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    history = [],
    ticketCount,
    supportTicketCount,
    hierarchyAssets = [],
  } = listOwnOemMachineHistory ?? {
    currentPage: 0,
    limit: 0,
    skip: 0,
    totalCount: 0,
    history: [],
    ticketCount: 0,
    supportTicketCount: 0,
    hierarchyAssets: [],
  };

  const handleFetchMore = ({ limit, skip }) => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          where: {
            searchQuery,
            ...(where || {}),
          },
        },
        selectedMachineIds,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedHistory = [
          ...previousResult.listOwnOemMachineHistory.history,
          ...fetchMoreResult.listOwnOemMachineHistory.history,
        ];

        const updates = {
          ...previousResult,
          listOwnOemMachineHistory: {
            ...previousResult.listOwnOemMachineHistory,
            ...fetchMoreResult.listOwnOemMachineHistory,
            history: mergedHistory,
          },
        };

        return updates;
      },
    });
  };

  return {
    history,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
    ticketCount,
    supportTicketCount,
    hierarchyAssets,
  };
};

export const useListOwnOemSharedAssetHistory = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  where = null,
  includeHierarchy = false,
  selectedMachineIds = undefined,
}: {
  limit?: number;
  skip?: number;
  searchQuery?: string;
  where?: any;
  includeHierarchy?: boolean;
  selectedMachineIds?: string[];
} = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      where: {
        searchQuery,
        ...(where || {}),
      },
    },
    includeHierarchy,
    selectedMachineIds,
  };

  const {
    data: { listOwnOemSharedAssetHistory = {} } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_SHARED_ASSET_HISTORY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    history = [],
    ticketCount,
    supportTicketCount,
    hierarchyAssets = [],
  } = listOwnOemSharedAssetHistory ?? {
    currentPage: 0,
    limit: 0,
    skip: 0,
    totalCount: 0,
    history: [],
    ticketCount: 0,
    supportTicketCount: 0,
    hierarchyAssets: [],
  };

  const handleFetchMore = ({ limit, skip }) => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          where: {
            searchQuery,
            ...(where || {}),
          },
        },
        selectedMachineIds,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedHistory = [
          ...previousResult.listOwnOemSharedAssetHistory.history,
          ...fetchMoreResult.listOwnOemSharedAssetHistory.history,
        ];

        const updates = {
          ...previousResult,
          listOwnOemSharedAssetHistory: {
            ...previousResult.listOwnOemSharedAssetHistory,
            ...fetchMoreResult.listOwnOemSharedAssetHistory,
            history: mergedHistory,
          },
        };

        return updates;
      },
    });
  };

  return {
    history,
    handleFetchMore,
    refetch,
    fetchMore,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
    ticketCount,
    supportTicketCount,
    hierarchyAssets,
  };
};

export const useGetAssetById = ({ id, skipCondition = false }) => {
  const {
    data: { getOwnOemAssetById } = {},
    error,
    loading,
  } = useQuery(GET_OWN_OEM_ASSET_BY_ID_QUERY, {
    variables: {
      id,
    },
    skip: !id || skipCondition,
  });

  return {
    error,
    loading,
    asset: getOwnOemAssetById,
  };
};

export const useGetAssetWithOrgById = ({
  id,
  connectionId,
  skipCondition = false,
}: {
  id?: string;
  /** The portal being viewed; decides which sharing grants apply to this asset. */
  connectionId?: string;
  skipCondition?: boolean;
}) => {
  const {
    data: { getOwnOemAssetById } = {},
    error,
    loading,
  } = useQuery(GET_SHARED_OEM_ASSET_BY_ID_QUERY, {
    variables: {
      id,
      isSharedAsset: true,
      connectionId,
    },
    skip: !id || skipCondition,
  });

  return {
    error,
    loading,
    asset: getOwnOemAssetById,
  };
};

export const useGetAssetsPartsById = ({
  id,
  skipCondition = false,
  isSharedAsset = false,
}: {
  id: string;
  skipCondition?: boolean;
  isSharedAsset?: boolean;
}) => {
  const {
    data: { getOwnOemAssetById: asset = {} } = {},
    loading,
    refetch,
  } = useQuery(GET_OWN_OEM_ASSET_PARTS_BY_ID_QUERY, {
    variables: {
      id,
      isSharedAsset,
    },
    skip: !id || skipCondition,
  });

  return {
    inventoryParts: asset?.inventoryParts,
    inventoryPartsLoading: loading,
    refetchInventoryParts: refetch,
  };
};

export const useGetAssetByUuid = ({
  uuid,
  skipCondition = false,
}: {
  uuid: string;
  skipCondition?: boolean;
}) => {
  const { data: { getOwnCustomerAssetByUuid: asset = {} } = {}, loading } =
    useQuery(GET_OWN_CUSTOMER_ASSET_BY_UUID_QUERY, {
      variables: {
        uuid,
      },
      skip: !uuid || skipCondition,
    });

  return { asset, loading };
};

export const useGetAssetPartsByUuid = ({
  uuid,
  skipCondition = false,
}: {
  uuid: string;
  skipCondition?: boolean;
}) => {
  const { data: { getOwnCustomerAssetByUuid: asset = {} } = {}, loading } =
    useQuery(GET_OWN_CUSTOMER_ASSET_PARTS_BY_UUID_QUERY, {
      variables: {
        uuid,
      },
      skip: !uuid || skipCondition,
    });

  return { assetParts: asset?.inventoryParts, loading };
};

export const useGetAssetFolderTokens = ({
  id,
  skipCondition = false,
}: {
  id: string;
  skipCondition?: boolean;
}) => {
  const { data, loading, refetch } = useQuery(
    GET_BOX_FOLDER_ACCESS_TOKEN_QUERY,
    {
      variables: {
        assetId: id,
      },
      skip: !id || skipCondition,
      fetchPolicy: "network-only",
      pollInterval: BOX_TOKEN_POLL_INTERVAL_MS,
    },
  );

  const tokenData = data?.getBoxFolderAccessToken;

  // Backward compatibility: old responses are plain strings, new ones are { [folderId]: token }
  const tokenMap: Record<string, string> | null =
    typeof tokenData === "string" ? null : tokenData ?? null;

  return {
    token: typeof tokenData === "string" ? tokenData : null,
    tokenMap,
    loading,
    refetch,
  };
};

export const useListAllOwnOemAssets = ({
  activeFilters = defaultFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  where = null,
  skipCondition = false,
  sort = [],
  preferNetwork = false,
  /**
   * Read through the customer portal's access-gated query instead of the OEM
   * one. Both return the same shape (the portal query aliases itself back to
   * `listAllOwnOemAssets`), so only the authorization differs.
   */
  useCustomerPortalQuery = false,
} = {}) => {
  const filterConditions = generateAssetsQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const variables = {
    ...(hasFilter
      ? {
          params: {
            where: {
              ...filterConditions,
              searchQuery,
              ...(where || {}),
            },
            limit,
            skip,
            sort,
          },
        }
      : {
          params: {
            limit,
            skip,
            sort,
            where: {
              searchQuery,
              ...(where || {}),
            },
          },
        }),
  };

  const {
    data,
    previousData,
    loading: assetsListLoading,
    refetch: assetsRefetch,
    fetchMore,
    error,
  } = useQuery(
    useCustomerPortalQuery
      ? LIST_CUSTOMER_PORTAL_SHARED_ASSETS_QUERY
      : LIST_ALL_OWN_OEM_ASSETS_QUERY,
    {
      variables,
      fetchPolicy: preferNetwork ? "network-only" : "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip: skipCondition,
    },
  );

  // Keep prior rows while sort/filter variables change — avoids full-table
  // skeleton flash (new variables miss cache until the network responds).
  const listAllOwnOemAssets =
    data?.listAllOwnOemAssets ?? previousData?.listAllOwnOemAssets ?? {};

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    assets = [],
  } = listAllOwnOemAssets;

  const handleFetchMore = ({ limit, skip }) => {
    fetchMore({
      variables: {
        ...(hasFilter
          ? {
              params: {
                where: {
                  ...filterConditions,
                  searchQuery,
                  ...(where || {}),
                },
                limit,
                skip,
                sort,
              },
            }
          : {
              params: {
                limit,
                skip,
                sort,
                where: {
                  searchQuery,
                  ...(where || {}),
                },
              },
            }),
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedAssets = [
          ...previousResult.listAllOwnOemAssets.assets,
          ...fetchMoreResult.listAllOwnOemAssets.assets,
        ];

        const updates = {
          ...previousResult,
          listAllOwnOemAssets: {
            ...previousResult.listAllOwnOemAssets,
            ...fetchMoreResult.listAllOwnOemAssets,
            assets: mergedAssets,
          },
        };

        return updates;
      },
    });
  };

  return {
    assets,
    handleFetchMore,
    assetsRefetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    assetsListLoading,
    error,
  };
};

export const useListOwnOemAssetsCalendar = ({
  activeFilters = defaultFilters,
  searchQuery = "",
  where = null,
  skipCondition = false,
  sort = EMPTY_SORT,
  limit = ITEMS_PER_PAGE,
}: {
  activeFilters?: any;
  searchQuery?: string;
  where?: Record<string, unknown> | null;
  skipCondition?: boolean;
  sort?: string[];
  limit?: number;
} = {}) => {
  // Same source of truth as the query where-clause — not a parallel "is active" guess.
  // (Empty chip defaults must not keep hierarchy on while filters are applied.)
  const filterConditions = useMemo(
    () => generateAssetsQueryWhereCond(activeFilters),
    [activeFilters],
  );
  const anyFilterActive = Object.keys(filterConditions).length > 0;
  const useHierarchicalView = !searchQuery && !anyFilterActive;

  const variables = useMemo(
    () => ({
      params: {
        limit,
        skip: 0,
        sort,
        where: {
          ...filterConditions,
          searchQuery,
          ...(useHierarchicalView ? {} : { calendarFlat: true }),
          ...(where || {}),
        },
      },
    }),
    [limit, sort, filterConditions, searchQuery, useHierarchicalView, where],
  );

  const {
    data: { listOwnOemAssetsCalendar = {} } = {},
    loading,
    refetch,
    fetchMore,
    error,
    networkStatus,
  } = useQuery(LIST_OWN_OEM_ASSETS_CALENDAR_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: skipCondition,
  });

  const {
    assets = [],
    tickets = [],
    totalCount = 0,
    rootCount = 0,
    ticketsTotalCount = 0,
  } = listOwnOemAssetsCalendar;

  // rootCount is cumulative after fetchMore merges (see updateQuery).
  const rootsLoaded = rootCount || 0;
  const hasMore = rootsLoaded < (totalCount || 0);

  // Rapid date/filter changes can leave stale rootCount visible while variables
  // update — never fetchMore until page-1 for the new query is ready.
  const paginationLocked =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch;
  const canFetchMore = hasMore && !paginationLocked && !loading;

  const variablesKey = useMemo(() => JSON.stringify(variables), [variables]);
  const fetchGenerationRef = useRef(0);
  const fetchMoreInFlightRef = useRef(false);

  useEffect(() => {
    fetchGenerationRef.current += 1;
    fetchMoreInFlightRef.current = false;
  }, [variablesKey]);

  const handleFetchMore = useCallback(() => {
    if (!canFetchMore || fetchMoreInFlightRef.current) return;

    const generation = fetchGenerationRef.current;
    fetchMoreInFlightRef.current = true;

    fetchMore({
      variables: {
        params: {
          ...variables.params,
          skip: rootsLoaded,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        fetchMoreInFlightRef.current = false;
        if (generation !== fetchGenerationRef.current) {
          return previousResult;
        }
        if (!fetchMoreResult?.listOwnOemAssetsCalendar) {
          return previousResult;
        }

        const prev = previousResult.listOwnOemAssetsCalendar || {};
        const next = fetchMoreResult.listOwnOemAssetsCalendar || {};
        const pageRootCount = next.rootCount || 0;

        const seenAssetIds = new Set(
          (prev.assets || []).map((asset: { _id: string }) => asset._id),
        );
        const mergedAssets = [
          ...(prev.assets || []),
          ...(next.assets || []).filter(
            (asset: { _id: string }) =>
              asset?._id && !seenAssetIds.has(asset._id),
          ),
        ];

        const seenTicketIds = new Set(
          (prev.tickets || []).map((ticket: { _id: string }) => ticket._id),
        );
        const mergedTickets = [
          ...(prev.tickets || []),
          ...(next.tickets || []).filter(
            (ticket: { _id: string }) =>
              ticket?._id && !seenTicketIds.has(ticket._id),
          ),
        ];

        return {
          ...previousResult,
          listOwnOemAssetsCalendar: {
            ...prev,
            ...next,
            assets: mergedAssets,
            tickets: mergedTickets,
            totalCount: next.totalCount ?? prev.totalCount,
            rootCount: (prev.rootCount || 0) + pageRootCount,
          },
        };
      },
    }).finally(() => {
      fetchMoreInFlightRef.current = false;
    });
  }, [canFetchMore, fetchMore, variables, rootsLoaded]);

  return {
    assets,
    tickets,
    totalCount,
    rootCount: rootsLoaded,
    ticketsTotalCount,
    hasMore: canFetchMore,
    handleFetchMore,
    useHierarchicalView,
    loading,
    networkStatus,
    paginationLocked,
    refetch,
    error,
  };
};

export const useListRequestableAssets = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  where = null,
  skipCondition = false,
  sort = [],
} = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      sort,
      where: {
        searchQuery,
        ...(where || {}),
      },
    },
  };

  const {
    data: { listRequestableAssets = {} } = {},
    loading: assetsListLoading,
    refetch: assetsRefetch,
    fetchMore,
  } = useQuery(LIST_REQUESTABLE_ASSETS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    assets = [],
  } = listRequestableAssets;

  const handleFetchMore = ({ limit, skip }) => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          sort,
          where: {
            searchQuery,
            ...(where || {}),
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedAssets = [
          ...previousResult.listRequestableAssets.assets,
          ...fetchMoreResult.listRequestableAssets.assets,
        ];

        const updates = {
          ...previousResult,
          listAllOwnOemAssets: {
            ...previousResult.listRequestableAssets,
            ...fetchMoreResult.listRequestableAssets,
            assets: mergedAssets,
          },
        };

        return updates;
      },
    });
  };

  return {
    assets,
    handleFetchMore,
    assetsRefetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    assetsListLoading,
  };
};

export const useListOwnOemKanbanAssets = ({
  activeFilters = defaultFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  sort = null,
  where = null,
} = {}) => {
  const filterConditions = generateAssetsQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const variables = {
    ...(hasFilter
      ? {
          params: {
            where: {
              ...filterConditions,
              searchQuery,
              ...(where || {}),
            },
            limit,
            skip,
            ...(sort ? { sort } : {}),
          },
        }
      : {
          params: {
            limit,
            skip,
            ...(sort ? { sort } : {}),
            where: {
              searchQuery,
              ...(where || {}),
            },
          },
        }),
  };

  const {
    data: { listOwnOemKanbanAssets = {} } = {},
    loading,
    refetch: kanbanAssetsRefetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_KANBAN_ASSETS, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    columns = [],
  } = listOwnOemKanbanAssets;

  return {
    columns,
    fetchMore,
    kanbanAssetsRefetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useGetAsset3DInfo = ({ id: assetId }: { id: string }) => {
  const { data, loading, error, refetch } = useQuery<
    {
      getAsset3DInfo: {
        asset3DToken: string;
        _3DViewerURL: string;
      };
    },
    { id: string }
  >(GET_ASSET_3D_INFO_QUERY, {
    variables: { id: assetId },
    // The token embeds asset details (e.g. serial number) and expires, so never
    // serve a previously cached one.
    fetchPolicy: "network-only",
  });

  return {
    asset3DInfo: data?.getAsset3DInfo,
    loading,
    error,
    refetch,
  };
};
